import { Heading, Section, Tag } from "@carbon/react";
import { FC, Fragment, ReactNode, useState } from "react";
import { generatePath, Link } from "react-router-dom";

import EmptyTableState from "src/components/DataStates/EmptyTableState.tsx";
import { ModalTransition } from "src/components/Modal";
import { getScenarioVersionUrl } from "src/dynamic-view-renderer/Widgets/ExecutionsWidget/getScenarioVersionUrl.ts";
import { RelatedWidgetConfiguration } from "src/dynamic-view-renderer/Widgets/RelatedCases/relatedCases.typings.ts";
import { AssociatedCasesModal } from "src/dynamic-view-renderer/Widgets/RulesWidget/AssociatedCasesModal.tsx";
import { WidgetDefinition } from "src/dynamic-view-renderer/Widgets/Widget.typings.ts";
import WidgetContainer, {
  WidgetContainerProps,
} from "src/dynamic-view-renderer/Widgets/WidgetContainer.tsx";
import { useFormatTypedValue } from "src/hooks/useFormatTypedValue";
import { useT } from "src/internationalization";
import { RuleExecutionResult, ScenarioExecutionResultWithCase } from "src/types/scenario";

export type RulesWidgetProps = {
  data?: {
    scenarioExecutionResult?: ScenarioExecutionResultWithCase | null;
  };
  configuration: RelatedWidgetConfiguration;
  customHeader?: ReactNode;
} & Omit<WidgetContainerProps, "children">;

const NUMBER_OF_CASES_TO_DISPLAY = 3;

type RulesHeaderProps = {
  scenarioExecutionResult?: ScenarioExecutionResultWithCase | null;
  widgetDefinition?: WidgetDefinition;
};
const RulesHeader: FC<RulesHeaderProps> = ({ scenarioExecutionResult, widgetDefinition }) => {
  const title = widgetDefinition?.title;
  const score = scenarioExecutionResult?.globalScore;
  const t = useT();
  const scenarioVersion = scenarioExecutionResult?.scenarioVersion;

  const description =
    scenarioVersion?.scenario?.name && getScenarioVersionUrl(scenarioExecutionResult) ? (
      <p className="text-textSecondary">
        {t("component.widget.rules.scenario")}
        {" : "}
        <Link to={getScenarioVersionUrl(scenarioExecutionResult) ?? ""} className="no-underline">
          {scenarioVersion?.scenario?.name} - {scenarioVersion?.name}
        </Link>
      </p>
    ) : (
      widgetDefinition?.description
    );

  return (
    <div>
      <Section level={4}>
        <Heading className="flex justify-between">
          {title ?? widgetDefinition?.title}
          {score !== undefined && (
            <Tag type="red" className="whitespace-nowrap">
              {t("component.widget.rules.score")} : {score}
            </Tag>
          )}
        </Heading>
      </Section>
      <div className="line-clamp-1">
        {description ? (
          <p className="text-textSecondary">{description}</p>
        ) : (
          <div className="h-6"></div>
        )}
      </div>
    </div>
  );
};

type RuleCasesProps = {
  openShowMore?: () => void;
  cases: RuleExecutionResult["cases"];
  inTable?: boolean;
};
export const RuleCases: FC<RuleCasesProps> = ({ cases, inTable, openShowMore }) => {
  const casesToDisplay = cases.slice(0, NUMBER_OF_CASES_TO_DISPLAY);
  const casesLeft = cases.length - NUMBER_OF_CASES_TO_DISPLAY;
  const hasMoreCases = casesLeft > 0;
  const t = useT();
  const formatTypedValue = useFormatTypedValue();

  return (
    <div className="flex items-center">
      {!inTable ? (
        <>
          <span className="text-sm text-textSecondary">{t("component.widget.rules.title")}</span>
          <span className="mx-1"> : </span>
        </>
      ) : null}
      <span>
        {casesToDisplay.map((caseRecord, index) => {
          const value = caseRecord.properties.find(
            (property) => property.property.code === "id"
          )?.value;
          const isLastCaseRecord = index === casesToDisplay.length - 1;
          return (
            !!value && (
              <Link
                key={caseRecord.id}
                to={generatePath(`/case/:caseTypeCode/:id`, {
                  caseTypeCode: caseRecord.typeCode,
                  id: caseRecord.id,
                })}
                className="no-underline text-sm"
              >
                {formatTypedValue(value)}
                {!isLastCaseRecord && ", "}
              </Link>
            )
          );
        })}
      </span>
      {inTable ? (
        hasMoreCases && (
          <span>
            <span className="text-sm text-textSecondary">, </span>
            <Tag
              className="border-hidden hover:cursor-pointer"
              type="blue"
              size="sm"
              {...(openShowMore && { onClick: openShowMore })}
            >
              +{casesLeft}
            </Tag>
          </span>
        )
      ) : (
        <span>
          <span className="text-sm text-textSecondary">, </span>
          <Tag
            className="border-hidden hover:cursor-pointer"
            type="blue"
            size="sm"
            {...(openShowMore && { onClick: openShowMore })}
          >
            {hasMoreCases ? `+${casesLeft}` : t("component.widget.rules.link").toLowerCase()}
          </Tag>
        </span>
      )}
    </div>
  );
};

type RuleDetailsProps = {
  ruleExecutionDetails: RuleExecutionResult;
  openShowMore: () => void;
};
export const RuleDetails: FC<RuleDetailsProps> = ({ ruleExecutionDetails, openShowMore }) => {
  const { rule, ruleScore, id, cases } = ruleExecutionDetails ?? {};
  const t = useT();
  const casesExist = cases.length > 0;

  return (
    <Fragment key={id}>
      {rule && (
        <div className="border-t border-solid border-borderSubtle02 flex flex-col py-4">
          <div className="flex justify-between flex-wrap last:pb-0">
            <h6>{rule.name}</h6>
            <Tag size="sm" type="purple" className="whitespace-nowrap">
              {t("component.widget.rules.score")} : {ruleScore}
            </Tag>
          </div>
          {casesExist && <RuleCases cases={cases} openShowMore={openShowMore} />}
        </div>
      )}
    </Fragment>
  );
};

const RulesWidget: FC<RulesWidgetProps> = ({
  data,
  configuration,
  customHeader,
  ...containerProps
}) => {
  const scenarioExecutionResult = data?.scenarioExecutionResult;
  const [selectedRulExecutionId, setRulExecutionId] = useState<string | undefined>(undefined);

  return (
    <>
      <WidgetContainer
        {...containerProps}
        header={
          customHeader ?? (
            <RulesHeader
              scenarioExecutionResult={scenarioExecutionResult}
              widgetDefinition={containerProps.widgetDefinition}
            />
          )
        }
      >
        <div className="flex flex-wrap flex-col">
          {scenarioExecutionResult?.ruleExecutionDetails?.map((ruleExecution) => (
            <RuleDetails
              key={ruleExecution.id}
              ruleExecutionDetails={ruleExecution}
              openShowMore={() => setRulExecutionId(ruleExecution.id)}
            />
          ))}
          {(scenarioExecutionResult === undefined ||
            scenarioExecutionResult?.ruleExecutionDetails.length === 0) && <EmptyTableState />}
        </div>
      </WidgetContainer>
      <ModalTransition>
        {selectedRulExecutionId && (
          <AssociatedCasesModal
            ruleExecutionId={selectedRulExecutionId}
            onClose={() => setRulExecutionId(undefined)}
          />
        )}
      </ModalTransition>
    </>
  );
};

export default RulesWidget;
